import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Upload, Plus, X } from "lucide-react";
import DashboardShell from "@/layouts/ProviderDashboardLayout";
import api from "@/lib/api";
import { useDocumentTitle } from "@/lib/useDocumentTitle";

const TONE = {
  submitted: "bg-slate-50 text-slate-700 border-slate-200",
  under_review: "bg-amber-50 text-amber-700 border-amber-200",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  verified: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected: "bg-rose-50 text-rose-700 border-rose-200",
  expired: "bg-rose-50 text-rose-700 border-rose-200",
};

async function upload(file, kind = "verification") {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("kind", kind);
  const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data.id;
}

export default function VerificationCenterPage() {
  const [verifications, setVerifications] = useState([]);
  const [licenses, setLicenses] = useState([]);
  const [insurance, setInsurance] = useState([]);
  const [showAdd, setShowAdd] = useState(null); // 'verification' | 'license' | 'insurance'
  const [form, setForm] = useState({});
  useDocumentTitle("Verification");

  const load = async () => {
    const [v, l, i] = await Promise.all([api.get("/verification/mine"), api.get("/licenses/mine"), api.get("/insurance/mine")]);
    setVerifications(v.data || []);
    setLicenses(l.data || []);
    setInsurance(i.data || []);
  };
  useEffect(() => { load(); }, []);

  const openAdd = (kind) => { setForm(kind === "verification" ? { kind: "identity_verified", document_ids: [] } : kind === "license" ? { license_type: "electrical", number: "", authority: "", document_ids: [] } : { carrier: "", policy_number: "", coverage_type: "general_liability", document_ids: [] }); setShowAdd(kind); };

  const onUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try {
      const id = await upload(file, showAdd);
      setForm((p) => ({ ...p, document_ids: [...(p.document_ids || []), id] }));
      toast.success("Uploaded");
    } catch (err) { toast.error("Upload failed"); }
  };

  const submit = async () => {
    try {
      if (showAdd === "verification") await api.post("/verification", form);
      else if (showAdd === "license") await api.post("/licenses", form);
      else if (showAdd === "insurance") await api.post("/insurance", form);
      toast.success("Submitted for review");
      setShowAdd(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.error || "Failed"); }
  };

  return (
    <DashboardShell>
      <div className="max-w-4xl" data-testid="verification-center">
        <div className="mb-6">
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">Provider · Verification</div>
          <h1 className="mt-2 font-display text-2xl md:text-3xl font-semibold text-[#071b22]">Verification, licenses & insurance</h1>
          <p className="text-[#252A34]/70 text-sm mt-1">Upload documents once and unlock verified badges + faster booking.</p>
        </div>

        <Section title="Verification requests" onAdd={() => openAdd("verification")} testid="verification-list" items={verifications} render={(v) => (
          <>
            <div className="font-medium text-[#071b22]">{v.kind.replace(/_/g, " ")}</div>
            <div className="text-xs text-[#252A34]/60">{v.documents?.length || 0} document(s) · {new Date(v.created_at).toLocaleDateString()}</div>
            {v.decision_note && <div className="text-xs text-[#252A34] mt-1"><b>Reviewer note:</b> {v.decision_note}</div>}
          </>
        )} />
        <Section title="Licenses" onAdd={() => openAdd("license")} testid="licenses-list" items={licenses} render={(l) => (
          <>
            <div className="font-medium text-[#071b22]">{l.license_type} · #{l.number}</div>
            <div className="text-xs text-[#252A34]/60">{l.authority} · expires {l.expires_on ? new Date(l.expires_on).toLocaleDateString() : "—"}</div>
          </>
        )} />
        <Section title="Insurance policies" onAdd={() => openAdd("insurance")} testid="insurance-list" items={insurance} render={(i) => (
          <>
            <div className="font-medium text-[#071b22]">{i.carrier} · {i.policy_number}</div>
            <div className="text-xs text-[#252A34]/60">{i.coverage_type} · ${(i.coverage_amount || 0).toLocaleString()} · expires {i.expires_on ? new Date(i.expires_on).toLocaleDateString() : "—"}</div>
          </>
        )} />

        {showAdd && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-[#071b22]/50 p-4">
            <div className="w-full max-w-lg rounded-3xl bg-white p-6 border border-slate-200" data-testid={`verify-form-${showAdd}`}>
              <div className="flex items-start justify-between">
                <div className="font-display text-lg font-semibold text-[#071b22] capitalize">Add {showAdd}</div>
                <button onClick={() => setShowAdd(null)} className="rounded p-1 hover:bg-slate-100"><X className="h-4 w-4" /></button>
              </div>
              <div className="mt-4 grid gap-3">
                {showAdd === "verification" && (
                  <select value={form.kind} onChange={(e) => setForm((p) => ({ ...p, kind: e.target.value }))} className="rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid="verify-kind">
                    {["identity_verified", "gcw_verified", "background_checked", "licensed_pro", "insured_pro"].map((k) => <option key={k}>{k}</option>)}
                  </select>
                )}
                {showAdd === "license" && [["license_type", "License type"], ["number", "License #"], ["authority", "Issuing authority"], ["state", "State"], ["expires_on", "Expires on (YYYY-MM-DD)"]].map(([k, l]) => (
                  <div key={k}>
                    <label className="text-xs text-[#252A34]/70">{l}</label>
                    <input value={form[k] || ""} onChange={(e) => setForm((p) => ({ ...p, [k]: e.target.value }))} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" data-testid={`lic-${k}`} />
                  </div>
                ))}
                {showAdd === "insurance" && [["carrier", "Carrier"], ["policy_number", "Policy #"], ["coverage_type", "Coverage type"], ["coverage_amount", "Coverage amount"], ["expires_on", "Expires on (YYYY-MM-DD)"]].map(([k, l]) => (
                  <div key={k}>
                    <label className="text-xs text-[#252A34]/70">{l}</label>
                    <input value={form[k] || ""} onChange={(e) => setForm((p) => ({ ...p, [k]: e.target.value }))} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" data-testid={`ins-${k}`} />
                  </div>
                ))}
                <div>
                  <label className="text-xs text-[#252A34]/70">Documents</label>
                  <div className="mt-1 flex items-center gap-2">
                    <label className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs cursor-pointer" data-testid="verify-upload-btn">
                      <Upload className="h-3 w-3" /> Upload
                      <input type="file" className="hidden" onChange={onUpload} />
                    </label>
                    <div className="text-xs text-[#252A34]/60">{form.document_ids?.length || 0} uploaded</div>
                  </div>
                </div>
              </div>
              <div className="mt-4 flex justify-end gap-2">
                <button onClick={() => setShowAdd(null)} className="rounded-full border border-slate-200 px-4 py-2 text-sm">Cancel</button>
                <button onClick={submit} className="rounded-full bg-[#165a71] text-white px-4 py-2 text-sm font-medium" data-testid="verify-submit">Submit</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardShell>
  );
}

function Section({ title, items, onAdd, render, testid }) {
  return (
    <section className="mb-8" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-lg font-semibold text-[#071b22]">{title}</h2>
        <button onClick={onAdd} className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium" data-testid={`${testid}-add`}>
          <Plus className="h-3 w-3" /> Add
        </button>
      </div>
      {items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-[#252A34]/60">Nothing here yet.</div>
      ) : (
        <ul className="rounded-2xl border border-slate-200 bg-white divide-y">
          {items.map((it) => (
            <li key={it.id} className="p-4 flex items-start gap-3">
              <div className="h-9 w-9 rounded-lg bg-[#165a71]/10 text-[#165a71] flex items-center justify-center"><ShieldCheck className="h-4 w-4" /></div>
              <div className="flex-1 min-w-0 text-sm">{render(it)}</div>
              <span className={`text-[10px] uppercase tracking-wider rounded-full border px-2 py-1 ${TONE[it.status] || "bg-slate-50"}`}>{it.status}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
